import { auth } from '../firebase';
import type { MarketQuote } from './marketDataService';

const endpointBase =
    import.meta.env?.VITE_QUOTE_ENDPOINT_BASE_URL ||
    import.meta.env?.VITE_MARKET_DATA_ENDPOINT_BASE_URL ||
    '';

const getFunctionUrl = (
    functionName: 'getQuoteForInstrument' | 'getQuoteByProviderKey' | 'getQuote' | 'getHistoricalByProviderKey'
) => {
    if (!endpointBase) {
        throw new Error('Missing API endpoint base URL for quotes.');
    }

    try {
        const url = new URL(endpointBase);
        if (url.hostname.endsWith('.a.run.app') && url.hostname.includes('-')) {
            const hostParts = url.hostname.split('-');
            hostParts[0] = functionName.toLowerCase();
            url.hostname = hostParts.join('-');
            url.pathname = '/';
            url.search = '';
            return url.toString();
        }
    } catch {
        // Fallback to path-based URL composition.
    }

    return `${endpointBase.replace(/\/+$/, '')}/${functionName}`;
};

export type ProviderHistoryPoint = { date: Date; close: number };

export const getHistoricalByProviderKey = async (
    source: 'fmp' | 'eodhd',
    providerId: string,
    opts: { from: string; to: string }
): Promise<ProviderHistoryPoint[]> => {
    const normalizedProviderId = providerId.trim().toUpperCase();
    if (!normalizedProviderId || !opts.from || !opts.to) return [];

    const authHeader = await getAuthHeader();
    const url = new URL(getFunctionUrl('getHistoricalByProviderKey'));
    url.searchParams.set('source', source);
    url.searchParams.set('providerId', normalizedProviderId);
    url.searchParams.set('from', opts.from);
    url.searchParams.set('to', opts.to);

    const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
            Authorization: authHeader,
        },
    });

    if (!response.ok) {
        if (response.status === 404 || response.status === 429) return [];
        const body = await response.text().catch(() => '');
        throw new Error(`Provider history fetch failed (${response.status}): ${body.slice(0, 160)}`);
    }

    const data = (await response.json()) as { points?: Array<{ date?: string; close?: number }> };
    const rows = Array.isArray(data?.points) ? data.points : [];
    return rows
        .map((row) => ({ date: new Date(String(row?.date || '')), close: Number(row?.close) }))
        .filter((point) => Number.isFinite(point.close) && !Number.isNaN(point.date.getTime()));
};

const getAuthHeader = async () => {
    const user = auth.currentUser;
    if (!user) {
        throw new Error('You must be signed in to fetch quotes.');
    }
    const token = await user.getIdToken();
    return `Bearer ${token}`;
};


const PROVIDER_QUOTE_CLIENT_TTL_MS = 45_000;
const PROVIDER_QUOTE_429_COOLDOWN_MS = 15_000;
const PROVIDER_QUOTE_ERROR_COOLDOWN_MS = 30_000;

type ProviderQuoteCacheEntry = {
    quote: MarketQuote | null;
    expiresAt: number;
};

type ProviderQuoteInFlightEntry = {
    promise: Promise<MarketQuote | null>;
};

const providerQuoteCache = new Map<string, ProviderQuoteCacheEntry>();
const providerQuoteInFlight = new Map<string, ProviderQuoteInFlightEntry>();
const providerQuoteCooldownUntil = new Map<string, number>();

const getProviderQuoteRequestKey = (source: 'fmp' | 'eodhd', providerId: string) => `${source}:${providerId}`;

type QuoteApiResponse = {
    providerId?: string;
    providerKey?: string;
    price?: number;
    rawPrice?: number;
    previousClose?: number;
    change?: number;
    changePct?: number;
    changePercent?: number;
    currency?: string;
    rawCurrency?: string;
    quoteUnit?: string;
    unitScale?: number;
    currencySymbol?: string;
    exchange?: string;
    asOf?: string;
    fetchedAt?: string;
};

const isDebugQuotesEnabled = () => {
    if (typeof window === 'undefined') return false;
    try {
        return window.localStorage.getItem('debugQuotes') === '1';
    } catch {
        return false;
    }
};

const logQuoteDebug = (quote: MarketQuote) => {
    if (!isDebugQuotesEnabled()) return;
    console.debug('[quotes] normalized live quote', {
        providerId: quote.providerId,
        rawPrice: quote.rawPrice,
        rawCurrency: quote.rawCurrency,
        normalizedPrice: quote.price,
        normalizedCurrency: quote.currency,
        unitScale: quote.unitScale,
    });
};

const normalizeApiQuote = (data: QuoteApiResponse, fallbackProviderId: string): MarketQuote | null => {
    const price = Number(data.price);
    if (!Number.isFinite(price)) return null;

    const previousClose = Number(data.previousClose);
    const change = Number(data.change);
    const changePercent = Number(data.changePercent ?? data.changePct);
    const providerId = data.providerId || data.providerKey || fallbackProviderId;
    const rawPrice = Number(data.rawPrice);
    const serverUnitScale = Number(data.unitScale);
    const normalizedQuote: MarketQuote = {
        providerId,
        rawPrice: Number.isFinite(rawPrice) ? rawPrice : price,
        rawCurrency: data.rawCurrency || null,
        price,
        previousClose: Number.isFinite(previousClose) ? previousClose : undefined,
        change: Number.isFinite(change) ? change : undefined,
        changePercent: Number.isFinite(changePercent) ? changePercent : undefined,
        currency: data.currency || 'UNKNOWN',
        asOf: data.asOf || new Date().toISOString(),
        fetchedAt: data.fetchedAt,
        unitScale: (serverUnitScale === 0.01 ? 0.01 : 1) as 1 | 0.01,
    };
    logQuoteDebug(normalizedQuote);
    return normalizedQuote;
};

export const getQuoteForInstrument = async (instrumentId: string): Promise<MarketQuote | null> => {
    const normalizedInstrumentId = instrumentId.trim();
    if (!normalizedInstrumentId) return null;

    const authHeader = await getAuthHeader();
    const url = new URL(getFunctionUrl('getQuoteForInstrument'));
    url.searchParams.set('instrumentId', normalizedInstrumentId);

    const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
            Authorization: authHeader,
        },
    });

    if (!response.ok) {
        if (response.status === 404 || response.status === 429) return null;
        const body = await response.text().catch(() => '');
        throw new Error(`Quote fetch failed (${response.status}): ${body.slice(0, 160)}`);
    }

    const data = (await response.json()) as QuoteApiResponse;
    return normalizeApiQuote(data, normalizedInstrumentId);

};

export const getQuoteByProviderKey = async (
    source: 'fmp' | 'eodhd',
    providerId: string
): Promise<MarketQuote | null> => {
    const normalizedProviderId = providerId.trim().toUpperCase();
    if (!normalizedProviderId) return null;

    const requestKey = getProviderQuoteRequestKey(source, normalizedProviderId);
    const now = Date.now();

    const cooldownUntil = providerQuoteCooldownUntil.get(requestKey) ?? 0;
    if (cooldownUntil > now) {
        return null;
    }

    const cached = providerQuoteCache.get(requestKey);
    if (cached && cached.expiresAt > now) {
        return cached.quote;
    }

    const inFlight = providerQuoteInFlight.get(requestKey);
    if (inFlight) {
        return inFlight.promise;
    }

    const fetchPromise = (async () => {
        const authHeader = await getAuthHeader();
        const url = new URL(getFunctionUrl('getQuoteByProviderKey'));
        url.searchParams.set('source', source);
        url.searchParams.set('providerId', normalizedProviderId);

        const response = await fetch(url.toString(), {
            method: 'GET',
            headers: {
                Authorization: authHeader,
            },
        });

        if (!response.ok) {
            if (response.status === 404) {
                if (source === 'eodhd') {
                    const fallbackProviderId = normalizedProviderId.split('.')[0]?.trim();
                    if (fallbackProviderId) {
                        const fallbackQuote = await getQuoteByProviderKey('fmp', fallbackProviderId);
                        providerQuoteCache.set(requestKey, {
                            quote: fallbackQuote,
                            expiresAt: Date.now() + PROVIDER_QUOTE_CLIENT_TTL_MS,
                        });
                        return fallbackQuote;
                    }
                }

                providerQuoteCache.set(requestKey, { quote: null, expiresAt: Date.now() + PROVIDER_QUOTE_CLIENT_TTL_MS });
                return null;
            }
            if (response.status === 429) {
                providerQuoteCooldownUntil.set(requestKey, Date.now() + PROVIDER_QUOTE_429_COOLDOWN_MS);
                return null;
            }
            providerQuoteCooldownUntil.set(requestKey, Date.now() + PROVIDER_QUOTE_ERROR_COOLDOWN_MS);
            const body = await response.text().catch(() => '');
            throw new Error(`Provider quote fetch failed (${response.status}): ${body.slice(0, 160)}`);
        }

        const data = (await response.json()) as QuoteApiResponse;
        const normalizedQuote = normalizeApiQuote(data, normalizedProviderId);
        if (!normalizedQuote) {
            providerQuoteCache.set(requestKey, { quote: null, expiresAt: Date.now() + PROVIDER_QUOTE_CLIENT_TTL_MS });
            return null;
        }

        providerQuoteCache.set(requestKey, {
            quote: normalizedQuote,
            expiresAt: Date.now() + PROVIDER_QUOTE_CLIENT_TTL_MS,
        });
        providerQuoteCooldownUntil.delete(requestKey);
        return normalizedQuote;
    })();

    providerQuoteInFlight.set(requestKey, { promise: fetchPromise });
    try {
        return await fetchPromise;
    } finally {
        providerQuoteInFlight.delete(requestKey);
    }
};

export const getLegacyQuote = async (symbol: string): Promise<MarketQuote | null> => {
    const instrumentKey = symbol.trim().toUpperCase();
    if (!instrumentKey) return null;
    const url = new URL(getFunctionUrl('getQuote'));
    url.searchParams.set('instrumentKey', instrumentKey);

    const response = await fetch(url.toString(), { method: 'GET' });
    if (!response.ok) {
        if (response.status === 404 || response.status === 429) return null;
        const body = await response.text().catch(() => '');
        throw new Error(`Legacy quote fetch failed (${response.status}): ${body.slice(0, 160)}`);
    }

    const data = (await response.json()) as QuoteApiResponse;
    return normalizeApiQuote(data, instrumentKey);

};
